import { skipToken } from '@reduxjs/toolkit/query';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { Link } from 'react-router-dom';

import { AuthenticatedPath } from '@shared/constants/routes.constants';
import { useBillingControllerCompanyQuery } from '@shared/services/apiService';
import { useAppSelector } from '@shared/store';
import { selectors } from '@shared/store/ducks';

import { billingDate, formatCreditUnits } from '../model/formatBillingUnits';

export const CompanyBillingStatus = () => {
  const companyId = useAppSelector(selectors.auth.getUser)?.companyId;
  const { data, isError } = useBillingControllerCompanyQuery(
    companyId ? { companyId } : skipToken,
    { refetchOnMountOrArgChange: true },
  );
  if (!companyId || data?.mode === 'legacy_manual') return null;
  return (
    <Paper variant="outlined" sx={{ mt: 3, p: 2.5, borderRadius: 3 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2">Company credits and call capacity</Typography>
          <Typography variant="h6" sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>
            {isError
              ? 'Billing status unavailable'
              : data?.balance && data.plan
                ? `${formatCreditUnits(data.totalAvailableUnits ?? data.balance.availableUnits, data.plan.creditScale)} credits available · Up to ${data.plan.concurrentLimit} simultaneous calls`
                : data?.status === 'expired'
                  ? 'Credit period expired'
                  : data?.status === 'trial_pending'
                    ? 'Trial awaiting activation'
                    : 'Checking company billing'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {isError
              ? 'Open Billing to refresh the company status before starting new work.'
              : data?.status === 'review'
                ? 'Billing needs operator attention before new calls can start.'
                : data?.mode === 'credit_managed' && !data.admissionEnabled
                  ? 'New calls are paused. Active calls can finish.'
                  : (data?.totalAvailableUnits ?? data?.balance?.availableUnits) === '0'
                    ? 'New unfunded calls are blocked. Active calls can finish.'
                    : 'The server checks funding and shared capacity again when a call starts.'}
          </Typography>
          {data && !isError && (
            <Typography variant="caption" color="text.secondary">
              Server snapshot: {billingDate(data.asOf)}
            </Typography>
          )}
        </Box>
        <Button
          component={Link}
          to={AuthenticatedPath.BILLING}
          variant="outlined"
          sx={{ alignSelf: { sm: 'center' } }}>
          View billing
        </Button>
      </Stack>
    </Paper>
  );
};
